import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
} from '@material-ui/core';
import moment from 'moment';
import DataTable from '../../../components/DataTable';
import ModuleCollectionRepository from '../module/ModuleCollectionRepository';
import { Education, EducationModule, Module } from '../../../types';
import SearchContext from '../../../components/search/SearchContext';
import SearchInput from '../../../components/search/SearchInput';
import StatusFlagIcon from '../StatusFlagIcon';
import ModuleTypes from '../module/ModuleTypes';
import statusLabels from '../statusLabels';
import EducationRepository from '../EducationRepository';

interface ModuleOverviewTableDialogProps {
  education: Education;
  open: boolean;
  onClose: () => void;
}

const ModuleOverviewTableDialog = (props: ModuleOverviewTableDialogProps) => {
  const { education, open, onClose } = props;
  const [query, setQuery] = useState<string>('');

  const [modules, setModules] = useState<EducationModule[] | undefined>(
    undefined,
  );

  const searchContextValue = useMemo(
    () => ({ query, setQuery }),
    [query, setQuery],
  );

  const repository = useMemo(
    () => new ModuleCollectionRepository(modules || []),
    [modules],
  );

  const columns = [
    {
      name: 'Naam',
      field: 'publishedName',
      sortable: true,
    },
    {
      name: 'Type',
      field: 'typeEducation',
      sortable: false,
      render: (module: Module) => ModuleTypes[module.type],
    },
    {
      name: 'Opdrachten',
      field: 'assignments',
      sortable: false,
      render: (module: EducationModule) => {
        const finished = module.assignments.filter((a) => a.finished).length;
        const total = module.assignments.length;

        return `${finished} / ${total}`;
      },
    },
    {
      name: 'Status',
      field: 'status',
      sortable: false,
      filter: {
        type: 'checkbox' as const,
        options: Object.entries(statusLabels).map(([, label]) => ({
          label,
          value: label,
        })),
      },
      render: (module: EducationModule) => {
        if (module.moduleType === 'practical') {
          return '-';
        }

        return <StatusFlagIcon module={module} />;
      },
    },
    {
      name: 'Startdatum',
      field: 'start_date',
      sortable: false,
      render: (module: Module) =>
        module.events &&
        module.events.length > 0 &&
        module.moduleType !== 'practical' &&
        !module.elearning
          ? moment(module.events[0].startDate).format('DD-MM-YYYY')
          : '-',
    },
  ];

  useEffect(() => {
    new EducationRepository()
      .getModules(education.id, ['IO'])
      .then((response) => setModules(response.data as EducationModule[]));
  }, [education.id]);

  if (!modules) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg">
      <DialogContent>
        <SearchContext.Provider value={searchContextValue}>
          <Box display="flex" justifyContent="flex-end" mb={2}>
            <SearchInput />
          </Box>
          <DataTable
            id="module-overview-table"
            repository={repository}
            columns={columns}
            defaultFilters={{
              query: '',
              filters: {},
              order: [
                {
                  field: 'publishedName',
                  order: 'asc',
                },
              ],
            }}
            persistFilters
            contained={false}
          />
        </SearchContext.Provider>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Sluiten</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModuleOverviewTableDialog;
